import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { listProjects } from '../../api/projects';
import { fmt } from '../../utils/format';
import type { GrandProjet } from '../../types/estimation';
import { useTranslation } from 'react-i18next';
import { InfoTip } from '../shared/InfoTip';
import styles from './BenchmarkPage.module.css';

interface BenchmarkMetric {
  key: string;
  field: keyof GrandProjet;
  unit?: string;
  higherIsBetter?: boolean;
}

const METRICS: BenchmarkMetric[] = [
  { key: 'logements', field: 'logements', higherIsBetter: true },
  { key: 'emplois', field: 'emplois', higherIsBetter: true },
  { key: 'sbpTotal', field: 'sbp_total', unit: 'm2', higherIsBetter: true },
  { key: 'area', field: 'ha_total', unit: 'ha', higherIsBetter: true },
  { key: 'horizon', field: 'horizon_years' },
];

/** Derived metrics computed from project data */
interface DerivedMetrics {
  density: number;
  sbp_per_logement: number;
  emplois_ratio: number;
  ius: number;
}

function derivedMetrics(p: GrandProjet): DerivedMetrics {
  const ha = Number(p.ha_total) || 1;
  const log = p.logements || 1;
  return {
    density: p.logements / ha,
    sbp_per_logement: p.sbp_total / log,
    emplois_ratio: p.emplois / log,
    ius: p.sbp_total / (ha * 10000),
  };
}

const DERIVED_METRICS: { key: keyof DerivedMetrics; tKey: string; unitKey: string }[] = [
  { key: 'density', tKey: 'density', unitKey: 'logPerHa' },
  { key: 'sbp_per_logement', tKey: 'sbpPerLogement', unitKey: 'm2' },
  { key: 'emplois_ratio', tKey: 'emploisPerLogement', unitKey: '' },
  { key: 'ius', tKey: 'ius', unitKey: '' },
];

function getVal(project: GrandProjet, key: keyof GrandProjet): number {
  const v = project[key];
  return typeof v === 'number' ? v : 0;
}

/** Compute percentile rank of value in array */
function percentileRank(value: number, allValues: number[]): number {
  const below = allValues.filter((v) => v < value).length;
  const equal = allValues.filter((v) => v === value).length;
  return ((below + equal * 0.5) / allValues.length) * 100;
}

/** Euclidean distance between two projects (normalized) */
function projectDistance(a: GrandProjet, b: GrandProjet, ranges: Record<string, { min: number; max: number }>): number {
  let sum = 0;
  const keys: (keyof GrandProjet)[] = ['logements', 'emplois', 'sbp_total', 'ha_total', 'horizon_years'];
  for (const k of keys) {
    const range = ranges[k];
    if (!range || range.max === range.min) continue;
    const av = (getVal(a, k) - range.min) / (range.max - range.min);
    const bv = (getVal(b, k) - range.min) / (range.max - range.min);
    sum += (av - bv) ** 2;
  }
  return Math.sqrt(sum);
}

/** Simple radar chart SVG */
function RadarChart({ labels, values, maxValues, color }: {
  labels: string[];
  values: number[];
  maxValues: number[];
  color: string;
}) {
  const n = labels.length;
  const cx = 150, cy = 150, r = 120;
  const angles = labels.map((_, i) => (i * 2 * Math.PI) / n - Math.PI / 2);

  const gridLevels = [0.25, 0.5, 0.75, 1.0];

  function point(angle: number, scale: number) {
    return { x: cx + Math.cos(angle) * r * scale, y: cy + Math.sin(angle) * r * scale };
  }

  const dataPoints = values.map((v, i) => {
    const scale = maxValues[i]! > 0 ? Math.min(v / maxValues[i]!, 1) : 0;
    return point(angles[i]!, scale);
  });

  const dataPath = dataPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') + ' Z';

  return (
    <div className={styles.radarCanvas}>
      <svg className={styles.radarSvg} viewBox="0 0 300 300">
        {/* Grid */}
        {gridLevels.map((level) => (
          <polygon
            key={level}
            points={angles.map((a) => {
              const p = point(a, level);
              return `${p.x},${p.y}`;
            }).join(' ')}
            fill="none"
            stroke="rgba(255,255,255,0.08)"
            strokeWidth={1}
          />
        ))}
        {/* Axes */}
        {angles.map((a, i) => {
          const p = point(a, 1);
          return (
            <line key={i} x1={cx} y1={cy} x2={p.x} y2={p.y} stroke="rgba(255,255,255,0.06)" strokeWidth={1} />
          );
        })}
        {/* Data polygon */}
        <polygon points={dataPath.replace(/[MLZ]/g, '').trim().replace(/\s+/g, ',')} fill={`${color}33`} stroke={color} strokeWidth={2} />
        {/* Data points */}
        {dataPoints.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r={3} fill={color} />
        ))}
        {/* Labels */}
        {angles.map((a, i) => {
          const labelP = point(a, 1.18);
          return (
            <text
              key={i}
              x={labelP.x}
              y={labelP.y}
              textAnchor="middle"
              dominantBaseline="central"
              fill="rgba(255,255,255,0.5)"
              fontSize={10}
            >
              {labels[i]}
            </text>
          );
        })}
      </svg>
    </div>
  );
}

export function BenchmarkPage() {
  const { t } = useTranslation(['benchmark', 'common']);
  const navigate = useNavigate();
  const [allProjects, setAllProjects] = useState<GrandProjet[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string>('');

  useEffect(() => {
    listProjects()
      .then((data) => {
        setAllProjects(data);
        // Default to first custom project
        const custom = data.find((p) => p.is_custom);
        if (custom) setSelectedId(custom.id);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const selected = useMemo(() => allProjects.find((p) => p.id === selectedId), [allProjects, selectedId]);
  const referenceProjects = useMemo(() => allProjects.filter((p) => !p.is_custom), [allProjects]);

  // Percentile rankings
  const rankings = useMemo(() => {
    if (!selected || referenceProjects.length === 0) return null;

    const pool = [...referenceProjects, selected];
    const result: Record<string, { value: number; percentile: number }> = {};

    for (const m of METRICS) {
      const vals = pool.map((p) => getVal(p, m.field));
      const val = getVal(selected, m.field);
      result[m.key] = { value: val, percentile: percentileRank(val, vals) };
    }

    const derivedPool = pool.map(derivedMetrics);
    const selDerived = derivedMetrics(selected);
    for (const dm of DERIVED_METRICS) {
      const vals = derivedPool.map((d) => d[dm.key]);
      result[dm.key] = { value: selDerived[dm.key], percentile: percentileRank(selDerived[dm.key], vals) };
    }

    return result;
  }, [selected, referenceProjects]);

  // Compute ranges for distance calculation
  const ranges = useMemo(() => {
    const r: Record<string, { min: number; max: number }> = {};
    const pool = [...referenceProjects, ...(selected ? [selected] : [])];
    const keys: (keyof GrandProjet)[] = ['logements', 'emplois', 'sbp_total', 'ha_total', 'horizon_years'];
    for (const k of keys) {
      const vals = pool.map((p) => getVal(p, k));
      r[k as string] = { min: Math.min(...vals), max: Math.max(...vals) };
    }
    return r;
  }, [referenceProjects, selected]);

  // Similar projects
  const similarProjects = useMemo(() => {
    if (!selected || referenceProjects.length === 0) return [];
    return referenceProjects
      .map((p) => ({ project: p, distance: projectDistance(selected, p, ranges) }))
      .sort((a, b) => a.distance - b.distance)
      .slice(0, 5);
  }, [selected, referenceProjects, ranges]);

  // Outliers (metrics above P90 or below P10)
  const outliers = useMemo(() => {
    if (!rankings) return [];
    const alerts: { tKey: string; percentile: number; direction: string }[] = [];
    const allMetrics = [
      ...METRICS.map((m) => ({ key: m.key, tKey: m.key })),
      ...DERIVED_METRICS.map((m) => ({ key: m.key, tKey: m.tKey })),
    ];

    for (const m of allMetrics) {
      const r = rankings[m.key];
      if (!r) continue;
      if (r.percentile >= 90) alerts.push({ tKey: m.tKey, percentile: r.percentile, direction: 'high' });
      else if (r.percentile <= 10) alerts.push({ tKey: m.tKey, percentile: r.percentile, direction: 'low' });
    }
    return alerts;
  }, [rankings]);

  // Radar chart data
  const radarData = useMemo(() => {
    if (!rankings) return null;
    const keys = DERIVED_METRICS.map((dm) => dm.key);
    const tKeys = DERIVED_METRICS.map((dm) => dm.tKey);
    const values = keys.map((k) => rankings[k]?.percentile ?? 50);
    const maxValues = keys.map(() => 100);
    return { tKeys, values, maxValues };
  }, [rankings]);

  if (loading) return <div className={styles.loading}>{t('common:loading')}</div>;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h2>{t('benchmark:title')}</h2>
          <div className={styles.headerSub}>
            {t('benchmark:subtitle', { count: referenceProjects.length })}
          </div>
        </div>
      </div>

      {/* Project selector */}
      <div className={styles.selector}>
        <div className={styles.selectorGroup}>
          <span className={styles.selectorLabel}>{t('benchmark:selectProject')}</span>
          <select
            className={styles.select}
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
          >
            <option value="">{t('benchmark:selectProjectPlaceholder')}</option>
            {allProjects.filter((p) => p.is_custom).map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
        <span className={styles.projectCount}>
          {t('benchmark:refProjectsCount', { count: referenceProjects.length })}
        </span>
      </div>

      {!selected && (
        <div className={styles.empty}>{t('benchmark:selectToSee')}</div>
      )}

      {selected && rankings && (
        <>
          {/* Outlier alerts */}
          {outliers.length > 0 && (
            <div className={styles.outlierSection}>
              <h3 className={styles.sectionTitle}>{t('benchmark:outlierAlerts')}<InfoTip text={t('benchmark:tooltips.outlierAlerts')} /></h3>
              {outliers.map((o) => (
                <div key={o.tKey} className={styles.outlierRow}>
                  <span className={styles.outlierIcon}>!</span>
                  <span>
                    {t('benchmark:outlierText', {
                      metric: t(`benchmark:${o.tKey}`),
                      direction: t(`benchmark:${o.direction === 'high' ? 'top' : 'bottom'}`),
                      pct: Math.round(o.direction === 'high' ? 100 - o.percentile : o.percentile),
                    })}
                    {' '}(P{Math.round(o.percentile)})
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Percentile ranking cards */}
          <div className={styles.percentileRow}>
            {[
              ...METRICS.map((m) => ({
                key: m.key,
                tKey: m.key,
                unit: m.unit,
              })),
              ...DERIVED_METRICS.map((d) => ({
                key: d.key,
                tKey: d.tKey,
                unit: d.unitKey,
              })),
            ].map((m) => {
              const r = rankings[m.key];
              if (!r) return null;
              const pct = Math.round(r.percentile);
              const colorClass = pct >= 75 ? styles.pHigh : pct >= 25 ? styles.pMid : styles.pLow;
              const unitLabel = m.unit ? t(`benchmark:${m.unit}`) : '';
              return (
                <div key={m.key} className={styles.percentileCard}>
                  <div className={styles.percentileLabel}>{t(`benchmark:${m.tKey}`)}</div>
                  <div className={styles.percentileValue}>
                    {unitLabel ? `${fmt(r.value)} ${unitLabel}` : fmt(r.value)}
                  </div>
                  <div className={`${styles.percentileRank} ${colorClass}`}>
                    P{pct}
                  </div>
                  <div className={styles.percentileBar}>
                    <div
                      className={styles.percentileFill}
                      style={{
                        width: `${pct}%`,
                        background: pct >= 75 ? '#6366f1' : pct >= 25 ? '#f39c12' : '#22d3ee',
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Radar chart + stats */}
          <div className={styles.radarSection}>
            <div className={styles.radarCard}>
              <h3 className={styles.sectionTitle}>{t('benchmark:percentileRadar')}<InfoTip text={t('benchmark:tooltips.radarChart')} /></h3>
              {radarData && (
                <RadarChart
                  labels={radarData.tKeys.map((k) => t(`benchmark:${k}`))}
                  values={radarData.values}
                  maxValues={radarData.maxValues}
                  color="#6366f1"
                />
              )}
            </div>
            <div className={styles.radarCard}>
              <h3 className={styles.sectionTitle}>{t('benchmark:projectSummary')}</h3>
              <div style={{ padding: 16, fontSize: 13 }}>
                <p><strong>{selected.name}</strong></p>
                <p style={{ color: 'var(--color-text-muted)', marginTop: 8 }}>
                  {selected.logements} {t('benchmark:logements')}, {selected.emplois} {t('benchmark:emplois')}
                </p>
                <p style={{ color: 'var(--color-text-muted)' }}>
                  {selected.ha_total ?? 0} ha, {selected.horizon_years} {t('benchmark:years')}
                </p>
                <p style={{ color: 'var(--color-text-muted)' }}>
                  Communes: {selected.communes.join(', ') || '\u2014'}
                </p>
                <p style={{ marginTop: 16, fontSize: 12, color: 'var(--color-text-muted)' }}>
                  {t('benchmark:benchmarkedAgainst', {
                    count: referenceProjects.length,
                    outliers: outliers.length,
                  })}
                </p>
              </div>
            </div>
          </div>

          {/* Similar projects */}
          {similarProjects.length > 0 && (
            <div className={styles.similarSection}>
              <h3 className={styles.sectionTitle}>{t('benchmark:mostSimilar')}<InfoTip text={t('benchmark:tooltips.mostSimilar')} /></h3>
              <div className={styles.similarGrid}>
                {similarProjects.map(({ project: p, distance }) => (
                  <div
                    key={p.id}
                    className={styles.similarCard}
                    onClick={() => navigate(`/app/reference-projects/${p.id}`)}
                  >
                    <div className={styles.similarName}>{p.name}</div>
                    <div className={styles.similarMeta}>
                      <span>{p.logements} {t('benchmark:logements')}</span>
                      <span>{p.emplois} {t('benchmark:emplois')}</span>
                      <span>{p.ha_total ?? 0} ha</span>
                    </div>
                    <div className={styles.similarScore}>
                      {t('benchmark:similarity', { pct: Math.max(0, (100 - distance * 50)).toFixed(0) })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
